import React from 'react';
import { makeStyles } from "@material-ui/core";
import { GetUAProfileMetaInfoResponse, OptionsTemplateInfo, PhoneDirectory } from "../../store/types/Devices";
import { Colors } from "../../styles/Colors";
import {Column} from 'react-table';
import {TFunction} from 'react-i18next';
import OverflowTooltip from "../../components/OverflowTooltip/OverflowTooltip";
import classNames from 'classnames';
import PermissionProvider from '../../components/PermissionProvider/PermissionProvider';
import { Permission, PermissionType } from '../../store/types/Permission';
import Switch from '../../components/Switch/Switch';
import { getRoute } from './NewDeviceProfileDialog.utils';
import { Routes } from '../../routes/routes';
import {ReactComponent as OpenExternalLink} from '../../assets/OpenExternalLink.svg';
import CustomizedIconButton from '../../components/IconButton/IconButton';

export type DevicePhoneBookTabProps = {
    profile?: OptionsTemplateInfo | null;
    uaProfileMetaInfo: GetUAProfileMetaInfoResponse | undefined;
};

export type DevicePhoneBookAssignItem = PhoneDirectory & {
    enabled: boolean;
    orderIndex: number;
}

export const generateColumns = (
    isLimitReached: boolean,
    t: TFunction<string>,
    classes: ReturnType<typeof useStyles>,
    permissionToPhoneBooks: PermissionType,
    onSwitchClick?: (value: boolean, object: DevicePhoneBookAssignItem) => void
): Column<DevicePhoneBookAssignItem>[] => {

    const drawValueSpan = (text?: string, customClass?: string) => {
        return (
            <div style={{overflow: 'hidden'}}>
                <OverflowTooltip
                    tooltip={text}
                    text={text}
                    classes={{text: classNames(classes.valueSpan, customClass)}}
                    copy={false}
                />
            </div>
        );
    };

    const onClickExternalLink = (id: number) => {
        const route = getRoute(Routes.DevicePhoneBookDetails, {id});
        window.open(route, '_blank')?.focus();
    };
    
    const columns: Column<DevicePhoneBookAssignItem>[] = [
        {
            Header: t<string>('common:active'),
            accessor: 'enabled',
            width: 0.5,
            Cell: function Cell(params) {
                const disabled = params.row.original.state !== 'active' // phone book not provisioned
                    || (isLimitReached && !params.row.original.enabled); //limit reached but phone book is not selected
                return (
                    <>
                        <PermissionProvider permission={Permission.Inventory.DeviceProfiles.value}>
                            <Switch
                                checked={params.row.original.enabled}
                                onChange={(event) => {
                                    onSwitchClick?.(event.target.checked, params.row.original);
                                }}
                                dataQa={'device-phone-book-item-switcher' + params.row.original.i_ua_config_directory}
                                dataTestId={'device-phone-book-item-switcher' + params.row.original.i_ua_config_directory}
                                disabled={disabled}
                            />
                        </PermissionProvider>
                    </>
                );
            },
        },
        {
            Header: t<string>('screens:devicePhoneBooks.name'),
            accessor: 'name',
            width: 2,
            Cell: function Cell(params) {
                return drawValueSpan(params.row.original.name, classes.nameLabel);
            },
        },
        {
            Header: t<string>('screens:devicePhoneBooks.status'),
            accessor: 'state',
            width: 2,
            Cell: function Cell(params) {
                const label = params.row.original.state == 'active'
                    ?  t<string>('screens:devicePhoneBooks.activeStatus')
                    : t<string>('screens:devicePhoneBooks.draftStatus');
                const textClass = classNames(
                    params.row.original.state == 'active' && classes.activeStatus,
                    params.row.original.state == 'draft' && classes.draftStatus
                )
                return drawValueSpan(label, textClass);
            },
        },
        {
            Header: t<string>('screens:devicePhoneBooks.entries'),
            accessor: 'total_records',
            width: 0.5,
            Cell: function Cell(params) {
                const mode = params.row.original.total_records + '';
                return drawValueSpan(mode, classes.centerText);
            },
        },
        {
            Header: '',
            accessor: 'i_ua_config_directory',
            width: 1,
            Cell: function Cell(params) {
                const original = params.row.original;
                if(permissionToPhoneBooks=== PermissionType.Hidden)
                {
                    return (<></>);
                }
                return (<>
                    <CustomizedIconButton
                        onClick={() => {
                            onClickExternalLink?.(original.i_ua_config_directory);
                        }}
                        dataTestId="navigate-to-item-button"
                        tooltipText={t<string>('common:edit')}
                    >
                        <OpenExternalLink />
                    </CustomizedIconButton>
                </>);
            },
        }
    ];

    return columns;
};

export const useStyles = makeStyles(() => ({
    rulesTableContainer: {
        width: 'calc(100% - 56px)',
        paddingRight: 32,
        paddingLeft: 24,
        marginTop: 2,
        background: 'transparent',
        border: 'none',
        overflowX: 'hidden',

        '& table': {
            background: 'transparent',
            border: 'none',
            overflowX: 'hidden',

            '& thead': {
                background: 'transparent',
                border: 'none',
            },

            '& tbody': {
                background: 'transparent',
                border: 'none',
            }
        }
    },
    draggableTableContainer: {
        '& table': {
            '& thead': {
                '& tr': {
                    '& > :first-child': {
                        marginLeft: 16,
                        marginRight: -16,
                    },

                    '& > :nth-child(2)': {
                        marginLeft: 8,
                        marginRight: -8,
                    },
                }
            },
            '& tbody': {
                '& tr': {
                    '& > :first-child': {
                        paddingLeft: 0,
                        paddingRight: 0,
                    },
                }
            }
        }
    },
    notDraggableTableContainer: {
        '& table': {
            '& thead': {
                '& tr': {
                    '& > :first-child': {
                        marginLeft: -4,
                    },

                    '& > :nth-child(2)': {
                        marginLeft: -28,
                    },
                }
            },
            '& tbody': {
                '& tr': {
                    '& > :first-child': {
                        paddingLeft: 0,
                        paddingRight: 0,
                    },
                }
            }
        }
    },
    valueSpan: {
        fontStyle: 'normal',
        fontWeight: 400,
        fontSize: 14,
        color: Colors.Text,
        maxHeight: '44px !important',
    },
    lastColumn: {
        color: Colors.Gray5
    },
    dragColumnStyle: {
        paddingLeft: 0,
        paddingRight: 0,
        width: '54px !important',

        '& svg': {
            marginLeft: '14px !important',
            width: 19.5
        }
    },
    addRuleButton: {
        marginLeft: 8,
        marginTop: 8
    },
    listBarContainer: {
        overflow: 'hidden'
    },
    listBarItem: {
        width: 'fit-content',
        minWidth: 21,
        height: 12,
        padding: '5px 8px 4px 8px',
        gap: 10,
        border: '1px solid rgba(245, 132, 31, 1)',
        borderRadius: 2,
        textTransform: 'uppercase',
        color: 'rgba(245, 132, 31, 1)',
        background: 'transparent',
        fontSize: 10,
        lineHeight: '12px',
        fontWeight: 400,
        marginBottom: 4,
    },
    
    itemsContainer: {
        '& .MuiFormControl-root': {
            width: '100%',
        },
        userSelect: 'none',
        paddingBottom: 126,
    },

    activeStatus: {
        color: 'rgba(0, 0, 0, 0.6)'
    },

    draftStatus: {
        color: 'rgba(196, 2, 2, 1)'
        
    },

    nameLabel: {
        fontWeight: 400,
        fontSize: 16,
        lineHeight: '20px',
        color: 'rgba(0, 0, 0, 0.87)'
    },

    centerText: {
        paddingLeft: 14,
    },

    limitContainer: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        paddingLeft: 24,
        marginTop: 30,
        marginBottom: 19,
    },

    limitLabel: {
        fontWeight: 400,
        fontSize: 16,
        lineHeight: '20px',
        verticalAlign: 'middle',
        color: 'rgba(70, 70, 70, 1)',
        marginTop: 4,
    },

    limitIcon: {
        color: `rgba(176, 176, 176, 1) !important`,
        '& svg': {
            '& path': {
                fill: `rgba(176, 176, 176, 1) !important`,
                fillOpacity: 1,
            },
        },
        marginRight: 12,
    },
}));